import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, CalendarDays, CheckCircle2, ChevronDown, Clock3, Download, FileCheck2, Hash, Loader2, RefreshCw, Search, User, XCircle, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const SearchResults = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, lang: language } = useLanguage();
    const queryParams = new URLSearchParams(location.search);
    const keywords = queryParams.get("keywords")?.trim() || "";
    const [results, setResults] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [searchValue, setSearchValue] = useState(keywords);
    const [downloadingId, setDownloadingId] = useState(null);
    const tr = (key, fallback) => t?.ui?.searchResults?.[key] || t?.[key] || fallback;
    const locale = language === "fr" ? (document.documentElement.lang === "fr" ? "fr-FR" : "en-GB") : "en-GB";
    useEffect(() => {
        setSearchValue(keywords);
    }, [keywords]);
    const formatDate = (dateString) => {
        if (!dateString)
            return "—";
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(locale);
    };
    const calculateExpiration = (endDate) => {
        if (!endDate)
            return "—";
        const end = new Date(endDate);
        if (Number.isNaN(end.getTime()))
            return "—";
        end.setFullYear(end.getFullYear() + 2);
        return end.toLocaleDateString(locale);
    };
    const participantName = (participant) => {
        if (!participant)
            return tr("unknown", "Inconnu");
        const fullName = [participant.firstName, participant.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
        return fullName || participant.name || tr("unknown", "Inconnu");
    };
    const safeFilename = (value) => String(value || "certificate")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "_");
    const fetchResults = async (signal) => {
        if (!keywords) {
            setResults([]);
            setError(tr("missingKeywords", "Saisissez un identifiant ou un nom à vérifier."));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await fetch(`${API_URL}/api/search?keywords=${encodeURIComponent(keywords)}`, { signal });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || tr("noResults", "Aucun résultat trouvé."));
            }
            const certifs = Array.isArray(data.certifs) ? data.certifs : [];
            setResults(certifs);
            setOpenId(certifs.length === 1 ? certifs[0]?._id : null);
            if (certifs.length === 0) {
                setError(tr("noMatching", "Aucun certificat correspondant n’a été trouvé."));
            }
        }
        catch (err) {
            if (err.name !== "AbortError") {
                console.error("Search error:", err);
                setError(err.message || tr("noResults", "Aucun résultat trouvé."));
            }
        }
        finally {
            if (!signal?.aborted)
                setLoading(false);
        }
    };
    useEffect(() => {
        const controller = new AbortController();
        fetchResults(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywords, language]);
    const handleSearch = (event) => {
        event.preventDefault();
        const value = searchValue.trim();
        if (!value)
            return;
        navigate(`/results?keywords=${encodeURIComponent(value)}`);
    };
    const handleDownload = async (certificateId, name) => {
        if (downloadingId)
            return;
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/login");
                return;
            }
            setDownloadingId(certificateId);
            const response = await fetch(`${API_URL}/api/certifs/download/${certificateId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem("token");
                        localStorage.removeItem("user");
                    }
                    navigate("/login");
                    return;
                }
                throw new Error("Download failed");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `${safeFilename(name)}.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error("Download error:", err);
            alert(`${tr("errorLabel", "Erreur")}: ${err.message}`);
        }
        finally {
            setDownloadingId(null);
        }
    };
    const summaryText = useMemo(() => {
        if (results.length === 1) {
            return `${tr("singleCertVerifiedPrefix", "Le certificat trouvé est")} ${tr("verified", "vérifié")} ${tr("and", "et")} ${tr("authentic", "authentique")}.`;
        }
        const translatedPrefix = tr("allCertsVerifiedPrefix", `Les ${results.length} certificats trouvés sont`);
        return `${translatedPrefix} ${tr("verified", "vérifiés")} ${tr("and", "et")} ${tr("authentic", "authentiques")}.`;
    }, [results.length, t]);
    return (<main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="mx-auto max-w-6xl">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 px-6 py-9 text-white shadow-2xl shadow-slate-950/15 sm:px-9 lg:px-12 lg:py-11">
          <div className="absolute -right-16 -top-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl"/>
          <div className="absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-blue-700/20 blur-3xl"/>

          <div className="relative grid gap-8 lg:grid-cols-[1fr_430px] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-cyan-200">
                <BadgeCheck size={15}/>{tr("publicVerification", "V\u00E9rification publique")}</div>
              <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
                {tr("verificationResultsTitle", "Résultats de vérification")}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">{tr("subtitle", "Consultez les informations publiques du certificat et confirmez son authenticit\u00E9 en quelques secondes.")}</p>
            </div>

            <form onSubmit={handleSearch} className="rounded-2xl border border-white/10 bg-white/10 p-2 backdrop-blur-sm">
              <div className="flex items-center gap-2 rounded-xl bg-white p-1.5">
                <Search className="ml-3 shrink-0 text-slate-400" size={19}/>
                <input value={searchValue} onChange={(event) => setSearchValue(event.target.value)} placeholder={tr("placeholder", "Identifiant, participant ou formation")} className="min-w-0 flex-1 bg-transparent px-1 py-2 text-sm text-slate-950 outline-none placeholder:text-slate-400"/>
                <button type="submit" disabled={!searchValue.trim()} aria-label={language === "fr" ? tr("searchButton", "Lancer la recherche") : tr("searchButton", "Run search")} className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                  <Search size={17}/>
                  {language === "fr" ? "Rechercher" : tr("searchLabel", "Search")}
                </button>
              </div>
            </form>
          </div>
        </section>

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-500">{tr("searchLabel", "Recherche")}</p>
            <h2 className="mt-1 text-xl font-black text-slate-950">
              “{keywords || "—"}”
            </h2>
          </div>
          {!loading && !error && (<div className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm">
              <FileCheck2 size={18} className="text-cyan-600"/>
              {results.length}{tr("result", "r\u00E9sultat")}{results.length > 1 ? "s" : ""}
            </div>)}
        </div>

        {loading ? (<div className="mt-8 flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white text-center shadow-sm">
            <Loader2 className="h-10 w-10 animate-spin text-cyan-600"/>
            <p className="mt-4 text-base font-bold text-slate-800">
              {tr("searchingCertificates", "Recherche des certificats...")}
            </p>
            <p className="mt-1 text-sm text-slate-500">{tr("loadingDetail", "V\u00E9rification des informations en cours.")}</p>
          </div>) : error ? (<div className="mt-8 flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-rose-200 bg-white px-6 text-center shadow-sm">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <XCircle size={34}/>
            </div>
            <h3 className="mt-5 text-xl font-black text-slate-950">{tr("noDisplayed", "Aucun certificat affich\u00E9")}</h3>
            <p className="mt-2 max-w-lg text-sm leading-6 text-slate-500">{error}</p>
            <button type="button" onClick={() => fetchResults()} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800">
              <RefreshCw size={17}/>
              {tr("trySearchingAgain", "Réessayer")}
            </button>
          </div>) : (<>
            <div className="mt-8 space-y-4">
              {results.map((cert, index) => {
                const isOpen = openId === cert._id;
                const name = participantName(cert.participant);
                return (<motion.article key={cert._id || index} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.05, 0.3) }} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-cyan-200 hover:shadow-lg hover:shadow-slate-200/60">
                    <button type="button" onClick={() => setOpenId(isOpen ? null : cert._id)} className="flex w-full items-center gap-4 px-4 py-4 text-left sm:px-6" aria-expanded={isOpen}>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-600 text-white shadow-lg shadow-cyan-600/15">
                        <FileCheck2 size={23}/>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-base font-black text-slate-950 sm:text-lg">
                            {cert.subTheme || cert.theme || tr("certificate", "Certificat")}
                          </h3>
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                            <CheckCircle2 size={13}/>
                            {tr("verified", "Vérifié")}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1.5">
                            <Hash size={14}/>
                            <span className="font-semibold text-slate-700">{cert.identifier || "—"}</span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <User size={14}/>
                            {name}
                          </span>
                        </div>
                      </div>

                      <ChevronDown size={20} className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (<motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
                          <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-5 sm:px-6">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                              <InfoCard icon={CalendarDays} label={tr("startDate", "Date de début")} value={formatDate(cert.start_date)}/>
                              <InfoCard icon={CalendarDays} label={tr("endDate", "Date de fin")} value={formatDate(cert.end_date)}/>
                              <InfoCard icon={Clock3} label={tr("duration", "Durée")} value={`${cert.duration ?? "—"} ${tr("days", "jours")}`}/>
                              <InfoCard icon={BadgeCheck} label={tr("expiration", "Expiration")} value={calculateExpiration(cert.end_date)}/>
                            </div>

                            <div className="mt-5 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{tr("participant", "Participant")}</p>
                                <p className="mt-1 truncate font-black text-slate-900">{name}</p>
                              </div>

                              <button type="button" disabled={downloadingId === cert._id} onClick={() => handleDownload(cert._id, name)} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
                                {downloadingId === cert._id ? (<Loader2 size={18} className="animate-spin"/>) : (<Download size={18}/>)}
                                {tr("download", "Télécharger")}
                              </button>
                            </div>
                          </div>
                        </motion.div>)}
                    </AnimatePresence>
                  </motion.article>);
            })}
            </div>

            <div className="mt-8 flex items-start gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <CheckCircle2 size={22}/>
              </div>
              <div>
                <p className="font-black">{tr("authenticity", "Authenticit\u00E9 confirm\u00E9e")}</p>
                <p className="mt-1 text-sm leading-6 text-emerald-800">{summaryText}</p>
              </div>
            </div>
          </>)}
      </div>
    </main>);
};
const InfoCard = ({ icon: Icon, label, value }) => (<div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-slate-400">
      <Icon size={16}/>
      <p className="text-xs font-semibold uppercase tracking-[0.1em]">{label}</p>
    </div>
    <p className="mt-2 text-sm font-black text-slate-900">{value}</p>
  </div>);
export default SearchResults;
